/* Customer portal — Dashboard / overview */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePortalAuth } from "@/context/PortalAuthContext";
import {
  Truck, FileText, Receipt, HelpCircle, MessageSquare, ArrowRight,
  Clock, CheckCircle2, AlertTriangle,
} from "lucide-react";

function Stat({ label, value, sub, icon: Icon, tone = "brand", testId, to }) {
  const toneMap = {
    brand: "border-primary-orange text-primary-orange bg-primary-orange/5",
    warn:  "border-amber-500 text-amber-600 bg-amber-50",
    good:  "border-emerald-500 text-emerald-600 bg-emerald-50",
    info:  "border-neutral-500 text-neutral-700 bg-neutral-100",
  };
  const inner = (
    <div data-testid={testId} className={`bg-white border-l-4 p-5 shadow-sm hover:shadow-md transition ${toneMap[tone]?.split(' ')[0]}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-2">{label}</div>
          <div className="text-3xl font-black">{value}</div>
          {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function PortalDashboard() {
  const { user } = usePortalAuth();
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/portal/dashboard");
      setData(r.data);
    } catch (e) { console.warn("portal dashboard load failed", e); }
  };
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

  const nextJob = data?.next_job;

  return (
    <div className="space-y-8" data-testid="portal-dashboard">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-500">Overview</div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h1 className="text-4xl lg:text-5xl font-black leading-tight">
            Welcome back,<br/>
            <span className="text-primary-orange">{user?.name || user?.customer_name}</span>
          </h1>
          <div className="text-xs text-neutral-500 mono">
            Account · <span className="uppercase">{user?.customer_type}</span>
            {user?.status === "pending" && (
              <span className="ml-2 inline-block px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-300 uppercase text-[10px] tracking-widest">
                Pending review
              </span>
            )}
          </div>
        </div>
      </div>

      {user?.customer_type !== "account" && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <b>Cash-account mode.</b> Your orders will be submitted as <b>quote requests</b> for dispatch to review before scheduling.
            Talk to us on <Link to="/portal/contact" className="underline font-bold">Contact Dispatch</Link> to upgrade to a monthly account.
          </div>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testId="stat-active-jobs" label="Active jobs"    value={data?.active_jobs ?? 0}       sub="Scheduled or in progress"  icon={Truck}         tone="brand" to="/portal/jobs" />
        <Stat testId="stat-unapproved"   label="Dockets to approve" value={data?.unapproved_dockets ?? 0} sub="Awaiting your OK"       icon={CheckCircle2}  tone="warn"  to="/portal/dockets" />
        <Stat testId="stat-unpaid"       label="Unpaid invoices"    value={data?.unpaid_invoices ?? 0}    sub={`$${(data?.balance_owing ?? 0).toLocaleString()} balance`} icon={Receipt} tone="warn"  to="/portal/invoices" />
        <Stat testId="stat-quotes"       label="Open quotes"        value={data?.pending_quotes ?? 0}     sub="Waiting on you or dispatch"  icon={HelpCircle} tone="info"  to="/portal/quotes" />
      </div>

      {/* Next job card */}
      {nextJob && (
        <div className="bg-white border border-neutral-200 p-6 shadow-sm" data-testid="next-job-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-neutral-500">Next scheduled delivery</div>
              <div className="text-2xl font-black mt-1">{nextJob.date} · {nextJob.time_required || "TBC"}</div>
            </div>
            <Link to="/portal/jobs" className="text-primary-orange text-sm font-bold uppercase tracking-widest inline-flex items-center gap-1 hover:opacity-80">
              Track live <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Site</div>
              <div className="font-bold">{nextJob.site_name || nextJob.site_address || "TBC"}</div>
              {nextJob.site_address && nextJob.site_name && <div className="text-neutral-500 text-xs mt-0.5">{nextJob.site_address}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Mix</div>
              <div className="font-bold mono">{nextJob.mix_code}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Quantity</div>
              <div className="font-bold mono">{nextJob.quantity_m3} m³</div>
            </div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { to: "/portal/orders/new", label: "Place a new order", icon: Truck, testId: "quick-place-order" },
          { to: "/portal/quotes",     label: "Request a quote",   icon: HelpCircle, testId: "quick-request-quote" },
          { to: "/portal/dockets",    label: "View dockets",       icon: FileText,   testId: "quick-view-dockets" },
          { to: "/portal/contact",    label: "Contact dispatch",   icon: MessageSquare, testId: "quick-contact" },
        ].map((a) => (
          <Link
            key={a.to}
            to={a.to}
            data-testid={a.testId}
            className="bg-[#0a0a0b] text-white p-5 flex items-center gap-4 hover:bg-primary-orange hover:text-black transition group"
          >
            <a.icon className="w-6 h-6 shrink-0 group-hover:scale-110 transition-transform" />
            <div className="text-sm font-bold uppercase tracking-widest leading-tight">{a.label}</div>
          </Link>
        ))}
      </div>

      {/* Recent deliveries */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-black">Recent deliveries</h2>
          <Link to="/portal/dockets" className="text-xs uppercase tracking-widest text-primary-orange font-bold hover:opacity-80">View all →</Link>
        </div>
        <div className="bg-white border border-neutral-200 divide-y divide-neutral-100 shadow-sm" data-testid="recent-deliveries">
          {(!data?.recent_deliveries || data.recent_deliveries.length === 0) ? (
            <div className="p-6 text-center text-neutral-500 text-sm">No recent deliveries yet.</div>
          ) : (
            data.recent_deliveries.map((d) => (
              <div key={d.id} className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary-orange/10 text-primary-orange flex items-center justify-center rounded">
                  <Truck className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">Docket {d.docket_no} · <span className="mono">{d.mix_code}</span></div>
                  <div className="text-xs text-neutral-500 truncate">{d.site_address}</div>
                </div>
                <div className="text-right">
                  <div className="mono text-sm font-bold">{d.quantity_m3} m³</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    {d.customer_approved ? <span className="text-emerald-600">Approved</span> : "Awaiting approval"}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
